import { useState, useEffect, useCallback } from 'preact/hooks';
import type { ComponentType } from 'preact';
import { authService, UserInfo } from './functions/auth/authService';
import { microsoftAuthService } from './functions/auth/microsoftAuthService';
import Login from './pages/Login';
import { Overview } from './pages/Overview';
import { UnitOverview } from './pages/UnitOverview';
import { FinancialStatements } from './pages/FinancialStatements';
import { ComingSoon } from './pages/ComingSoon';
import { GeospatialIntelligence } from './pages/GeospatialIntelligence';
import { Sidebar } from './components/Sidebar';
import { Header } from './components/Header';
import { FilterProvider, useFilter } from './contexts/FilterContext';
import { useHashRoute, type AppRoute } from './routing';

const ROUTE_PAGES: Record<AppRoute, ComponentType> = {
  overview: Overview,
  'unit-overview': UnitOverview,
  'financial-statements': FinancialStatements,
  'geospatial-intelligence': GeospatialIntelligence,
  'coming-soon': ComingSoon,
};

export function App() {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const { route: currentPage, navigate } = useHashRoute();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const microsoftUser = await microsoftAuthService.handleRedirectCallback();
      if (microsoftUser) {
        setUser(microsoftUser);
        setLoading(false);
        return;
      }
      if (authService.isAuthenticated()) {
        try {
          const userInfo = await authService.verifyToken();
          setUser(userInfo);
        } catch (error) {
          console.error('Token verification failed:', error);
          authService.logout();
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  useEffect(() => {
    const savedState = localStorage.getItem('sidebarCollapsed');
    if (savedState !== null) {
      setSidebarCollapsed(JSON.parse(savedState));
    }
  }, []);

  const handleLoginSuccess = (userInfo: UserInfo) => {
    setUser(userInfo);
  };

  const handleLogout = () => {
    authService.logout();
    setUser(null);
    window.location.href = window.location.origin;
  };

  const handlePageChange = (page: AppRoute) => {
    navigate(page);
  };

  const toggleSidebar = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', JSON.stringify(newState));
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      {user ? (
        <FilterProvider>
          <AppContent
            user={user}
            sidebarCollapsed={sidebarCollapsed}
            currentPage={currentPage}
            onToggleSidebar={toggleSidebar}
            onPageChange={handlePageChange}
            onLogout={handleLogout}
          />
        </FilterProvider>
      ) : (
        <Login onLoginSuccess={handleLoginSuccess} />
      )}
    </div>
  );
}

interface AppContentProps {
  user: UserInfo;
  sidebarCollapsed: boolean;
  currentPage: AppRoute;
  onToggleSidebar: () => void;
  onPageChange: (page: AppRoute) => void;
  onLogout: () => void;
}

function AppContent({
  user,
  sidebarCollapsed,
  currentPage,
  onToggleSidebar,
  onPageChange,
  onLogout,
}: AppContentProps) {
  const { initializeFilters, selectedClient } = useFilter();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);
  const toggleMobileNav = useCallback(() => setMobileNavOpen((open) => !open), []);

  const handlePageChange = (page: AppRoute) => {
    closeMobileNav();
    onPageChange(page);
  };

  const handleLogout = () => {
    closeMobileNav();
    onLogout();
  };

  useEffect(() => {
    if (user) {
      initializeFilters();
    }
  }, [user?.email, initializeFilters]);

  useEffect(() => {
    closeMobileNav();
  }, [currentPage, closeMobileNav]);

  const PageComponent = ROUTE_PAGES[currentPage];

  return (
    <div className={`overview-container sidebar-layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {mobileNavOpen && (
        <button
          type="button"
          className="sidebar-backdrop is-visible"
          aria-label="Close navigation menu"
          onClick={closeMobileNav}
        />
      )}
      <Sidebar
        collapsed={sidebarCollapsed}
        mobileOpen={mobileNavOpen}
        onToggle={onToggleSidebar}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        onLogout={handleLogout}
      />
      <div className={`main-content${currentPage === 'geospatial-intelligence' ? ' main-content--fill' : ''}`}>
        <Header
          user={user}
          userName={user.name}
          selectedClient={selectedClient ?? undefined}
          currentPage={currentPage}
          onToggleMobileNav={toggleMobileNav}
        />
        <PageComponent />
      </div>
    </div>
  );
}
